import React from 'react';
import type { SubmissionStatus } from '../../types/MainType.type';
import { GeoLevel } from '../../types/database';

interface StageProgressionProps {
  status: SubmissionStatus;
  error: string | null;
  onErrorDismiss: () => void;
  targetGeoLevels?: GeoLevel[] | string[] | string;
  originOfficeName?: string;
  originGeoLevel?: GeoLevel | string;
  reviewingOfficeName?: string;
  reviewingGeoLevel?: GeoLevel | string;
  auditLogs?: unknown[];
  stageHistory?: {
    action?: string;
    comments?: string;
    attachments?: unknown[];
    performedBy?: {
      id?: string;
      name?: string;
      office?: {
        name?: string;
        level?: GeoLevel | string;
      };
    };
  }[];
}

const GEO_LEVEL_MAP: Record<string, string> = {
  'NATIONAL': 'ရုံးချုပ်',
  'STATE': 'ပြည်နယ်/တိုင်း',
  'DISTRICT': 'ခရိုင်',
  'TOWNSHIP': 'မြို့နယ်',
};

const getCurrentUserId = (): string | undefined => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return typeof user?.id === 'string' ? user.id : undefined;
  } catch (error) {
    console.error("Unable to read the current user's context:", error);
    return undefined;
  }
};

export function StageProgression({
  status,
  error,
  onErrorDismiss,
  targetGeoLevels = [],
  originOfficeName,
  originGeoLevel,
  reviewingOfficeName,
  reviewingGeoLevel,
  auditLogs = [],
  stageHistory = [],
}: StageProgressionProps) {
  const [currentUserId] = React.useState(getCurrentUserId);
  const [showAllReviews, setShowAllReviews] = React.useState(false);
  const [showAllAttachments, setShowAllAttachments] = React.useState(false);
  const visibleReviewLimit = 2;

  const formatGeoText = (levels: GeoLevel[] | string[] | string): string => {
    if (typeof levels === 'string') return levels;

    if (Array.isArray(levels)) {
      return levels
        .map((level) => GEO_LEVEL_MAP[level as string] || level)
        .filter(Boolean)
        .join(' | ');
    }

    return '';
  };

  const formatReviewText = (): string[] => {
    const originOfficeText = formatOfficeText({
      name: originOfficeName,
      level: originGeoLevel,
    });
    const historyReviews = [...new Set(
      stageHistory
        .filter(
          (history) =>
            history.action === 'UNDER_REVIEW' ||
            (history.action !== 'ATTACH' &&
              history.action !== 'ATTACHED' &&
              Boolean(history.performedBy?.office))
        )
        .map((history) => formatOfficeText(history.performedBy?.office))
        .filter((officeText) => officeText && officeText !== originOfficeText)
    )];

    const formattedCurrentReview = [
      reviewingGeoLevel
        ? GEO_LEVEL_MAP[reviewingGeoLevel] || reviewingGeoLevel
        : '',
      reviewingOfficeName,
    ]
      .filter(Boolean)
      .join(' - ');

    if (
      formattedCurrentReview &&
      formattedCurrentReview !== originOfficeText &&
      !historyReviews.includes(formattedCurrentReview)
    ) {
      historyReviews.push(formattedCurrentReview);
    }

    return historyReviews;
  };

  const formatOfficeText = (office?: {
    name?: string;
    level?: GeoLevel | string;
  }): string => {
    const formattedGeo = office?.level
      ? GEO_LEVEL_MAP[office.level] || office.level
      : '';
    return [formattedGeo, office?.name].filter(Boolean).join(' - ');
  };

  const getAuditLogText = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(getAuditLogText).join(' ');
    if (value && typeof value === 'object') {
      return Object.values(value).map(getAuditLogText).join(' ');
    }
    return '';
  };

  const getAuditLogActorId = (log: Record<string, unknown>): string | undefined => {
    const actor = log.performedBy || log.user || log.createdBy || log.actor;
    if (actor && typeof actor === 'object' && 'id' in actor) {
      const id = (actor as { id?: unknown }).id;
      if (typeof id === 'string') return id;
    }

    const id = log.performedById || log.userId || log.createdById || log.actorId;
    return typeof id === 'string' ? id : undefined;
  };

  const getAuditLogOffice = (candidate: unknown) => {
    if (!candidate || typeof candidate !== 'object') return undefined;
    const record = candidate as Record<string, unknown>;
    const office = record.office;
    if (office && typeof office === 'object') {
      return office as { name?: string; level?: GeoLevel | string };
    }
    if (typeof record.officeName === 'string' || typeof record.officeLevel === 'string') {
      return {
        name: typeof record.officeName === 'string' ? record.officeName : undefined,
        level: typeof record.officeLevel === 'string' ? record.officeLevel : undefined,
      };
    }
    return undefined;
  };

  const attachmentAuditLogs = auditLogs.filter((entry) => {
    const logText = getAuditLogText(entry).toLowerCase();
    if (logText.includes('#attachment:')) return true;
    if (!entry || typeof entry !== 'object') return false;

    const log = entry as Record<string, unknown>;
    return [log.action, log.status, log.type, log.event].some(
      (value) => typeof value === 'string' && /attach|upload|file/i.test(value)
    );
  });
  const attachmentStageHistory = stageHistory.filter((history) =>
    /attach|upload|file/i.test(history.action || '') ||
    (history.comments || '').toLowerCase().includes('#attachment:') ||
    (Array.isArray(history.attachments) && history.attachments.length > 0)
  );
  const attachmentHistory = [...new Set(
    attachmentAuditLogs
      .flatMap((entry) => {
        const log =
          entry && typeof entry === 'object'
            ? (entry as Record<string, unknown>)
            : undefined;
        const actor =
          log?.performedBy || log?.user || log?.createdBy || log?.actor;
        const actorId = log ? getAuditLogActorId(log) : undefined;
        const actorName =
          actor && typeof actor === 'object' && 'name' in actor
            ? (actor as { name?: unknown }).name
            : undefined;
        const directOffice = getAuditLogOffice(actor) || (log && getAuditLogOffice(log));
        if (directOffice) return [formatOfficeText(directOffice)];

        const matchingHistory = stageHistory.filter((history) => {
          if (!history.performedBy?.office) return false;
          if (actorId) return history.performedBy.id === actorId;
          if (typeof actorName === 'string') {
            return history.performedBy.name === actorName;
          }
          return attachmentStageHistory.includes(history);
        });
        if (matchingHistory.length > 0) {
          return matchingHistory.map((history) =>
            formatOfficeText(history.performedBy?.office)
          );
        }

        if (!actorId && typeof actorName !== 'string') {
          return attachmentStageHistory.map((history) =>
            formatOfficeText(history.performedBy?.office)
          );
        }

        return [];
      })
      .filter(Boolean)
  )];
  const geoText = formatGeoText(targetGeoLevels);
  const originOfficeText = formatOfficeText({
    name: originOfficeName,
    level: originGeoLevel,
  });
  const submittedText = [geoText, originOfficeText].filter(Boolean);
  const reviewText = formatReviewText();
  const visibleReviewText = showAllReviews
    ? reviewText
    : reviewText.slice(0, visibleReviewLimit);
  const visibleAttachmentHistory = showAllAttachments
    ? attachmentHistory
    : attachmentHistory.slice(0, visibleReviewLimit);

  const stages: { label: string; subLabels?: string[]; key: SubmissionStatus }[] = [
    {
      label: 'မူကြမ်း',
      subLabels: originOfficeText ? [originOfficeText] : undefined,
      key: 'DRAFT',
    },
    {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      subLabels: originOfficeText ? [originOfficeText] : undefined,
      key: 'APPROVED',
    },
    {
      label: 'တင်ပြပြီး',
      subLabels: submittedText.length > 0 ? submittedText : undefined,
      key: 'SUBMITTED',
    },
    { 
      label: 'ကြည့်ရှုခဲ့သည်',
      subLabels: reviewText,
      key: 'UNDER_REVIEW' 
    },
    { label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      subLabels: attachmentHistory, key: 'ATTACHED' },
  ];

  const getStageIndex = (status: SubmissionStatus) => {
    switch (status) {
      case 'APPROVED': return 1;
      case 'SUBMITTED': return 2;
      case 'UNDER_REVIEW': return 3;
      case 'ATTACHED': return 4;
      case 'DRAFT':
      default: return 0;
    }
  };

  const currentIndex = getStageIndex(status);
  const currentUserHasCompletedReviewOrAttachment = Boolean(
    currentUserId &&
      stageHistory.some(
        (history) =>
          history.performedBy?.id === currentUserId &&
          ['UNDER_REVIEW', 'ATTACH', 'ATTACHED'].includes(history.action || '')
      )
  );
  const displayedStatus =
    currentUserHasCompletedReviewOrAttachment ? 'ATTACHED' : status;
  const displayedCurrentIndex = currentUserHasCompletedReviewOrAttachment
    ? 4
    : currentIndex;

  return (
    <div className="space-y-2">
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-lg py-1.5 px-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span>⚠️</span>
            <span className="font-medium">{error}</span>
          </div>
          <button onClick={onErrorDismiss} className="text-rose-600 hover:text-rose-800 font-bold px-1 text-xs">
            ✕
          </button>
        </div>
      )}

      {/* Reduced padding from p-4 to px-3 py-2.5 */}
      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 shadow-sm">
        {/* Tighter header spacing */}
        <div className="flex justify-between items-center mb-2.5">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            လုပ်ငန်းစဉ် အခြေအနေ (Stage Progression)
          </p>
          <span
            className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
              displayedStatus === 'ATTACHED'
                ? 'bg-emerald-100 text-emerald-800'
                : displayedStatus === 'APPROVED'
                ? 'bg-teal-100 text-teal-800'
                : displayedStatus === 'SUBMITTED'
                ? 'bg-indigo-100 text-indigo-800'
                : displayedStatus === 'UNDER_REVIEW'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-slate-200 text-slate-700'
            }`}
          >
            {displayedStatus === 'ATTACHED'
              ? 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်'
              : displayedStatus === 'UNDER_REVIEW'
              ? 'ကြည့်ရှုနေသည်'
              : displayedStatus === 'SUBMITTED'
              ? 'တင်ပြပြီး'
              : displayedStatus === 'APPROVED'
              ? 'ကိုယ်တိုင်အတည်ပြုပြီး'
              : 'မူကြမ်း'}
          </span>
        </div>

        {/* Scaled-down timeline layout */}
        <div className="flex items-start justify-between relative">
          {stages.map((stage, idx) => {
            const isCompleted = idx < displayedCurrentIndex;
            const isCurrent = idx === displayedCurrentIndex;
            const visibleSubLabels =
              stage.key === 'UNDER_REVIEW'
                ? visibleReviewText
                : stage.key === 'ATTACHED'
                ? visibleAttachmentHistory
                : stage.subLabels || [];

            return (
              <React.Fragment key={stage.key}>
                <div className="flex flex-col items-center z-10 flex-1">
                  {/* Smaller step circle (w-6 h-6 instead of w-8 h-8) */}
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] transition-colors shadow-sm ${
                      isCompleted
                        ? 'bg-emerald-600 text-white'
                        : isCurrent
                        ? 'bg-blue-600 text-white ring-2 ring-blue-100'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {isCompleted ? '✓' : idx + 1}
                  </div>
                  
                  <span
                    className={`text-[10px] sm:text-[11px] mt-1 font-medium text-center px-0.5 leading-tight ${
                      isCurrent
                        ? 'text-blue-700 font-bold'
                        : isCompleted
                        ? 'text-slate-700 font-semibold'
                        : 'text-slate-400'
                    }`}
                  >
                    {stage.label}
                  </span>

                  {stage.subLabels && (
                    <div
                      className={`w-full text-[9px] sm:text-[10px] font-normal text-left mt-0.5 px-0.5 leading-tight ${
                        isCurrent
                          ? 'text-blue-600'
                          : isCompleted
                          ? 'text-slate-500'
                          : 'text-slate-400'
                      }`}
                    >
                      {visibleSubLabels.map((subLabel, index) => (
                        <span className="block" key={`${subLabel}-${index}`}>
                          ({subLabel})
                        </span>
                      ))}
                      {stage.key === 'UNDER_REVIEW' && reviewText.length > visibleReviewLimit && (
                        <button
                          type="button"
                          onClick={() => setShowAllReviews((showAll) => !showAll)}
                          className="mt-1 font-semibold text-blue-700 hover:text-blue-900 underline"
                        >
                          {showAllReviews
                            ? 'See less'
                            : `See more (${reviewText.length - visibleReviewLimit})`}
                        </button>
                      )}
                      {stage.key === 'ATTACHED' && attachmentHistory.length > visibleReviewLimit && (
                        <button
                            type="button"
                            onClick={() => setShowAllAttachments((showAll) => !showAll)}
                            className="mt-1 font-semibold text-blue-700 hover:text-blue-900 underline"
                        >
                            {showAllAttachments
                              ? 'See less'
                              : `See more (${attachmentHistory.length - visibleReviewLimit})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {idx < stages.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mt-3 transition-colors ${
                      idx < displayedCurrentIndex ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}